'use client';

import { useSendContactMessage } from '@/graphql/contact';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { FaEnvelope, FaUser, FaComment } from 'react-icons/fa';
import { toast } from 'react-toastify';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

export default function ContactUs() {
  const t = useTranslations('contactUs');

  const { sendMessage, loading } = useSendContactMessage();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const contact = {
      name: (form.elements[0] as HTMLInputElement).value,
      email: (form.elements[1] as HTMLInputElement).value,
      message: (form.elements[2] as HTMLTextAreaElement).value,
    };

    try {
      await sendMessage(contact);
      toast.success('تم إرسال الرسالة بنجاح');
      form.reset();
    } catch (error) {
      toast.error('حدث خطأ أثناء إرسال الرسالة.');
    }
  };

  return (
    <section
      className="relative w-full min-h-screen flex items-center justify-center bg-cover bg-center"
      style={{ backgroundImage: "url('/imgs/contact-us.jpeg')" }}
    >
      <div className="absolute inset-0 bg-black/30 -z-10" />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.3 }}
        className="w-full max-w-3xl p-10 md:p-14 bg-white/90 rounded-3xl shadow-2xl backdrop-blur-sm"
      >
        <motion.h2
          variants={itemVariants}
          className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 text-center"
        >
          {t('title')}
        </motion.h2>

        <motion.p
          variants={itemVariants}
          className="text-gray-600 mb-10 text-center text-lg"
        >
          {t('subtitle')}
        </motion.p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-4 border rounded-full px-6 py-4 focus-within:ring-2 focus-within:ring-olive-dark transition-all"
          >
            <FaUser className="text-gray-400 text-xl" />
            <input
              type="text"
              placeholder={t('fullName')}
              className="w-full outline-none text-gray-700 placeholder-gray-400 bg-transparent"
              required
            />
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="flex items-center gap-4 border rounded-full px-6 py-4 focus-within:ring-2 focus-within:ring-olive-dark transition-all"
          >
            <FaEnvelope className="text-gray-400 text-xl" />
            <input
              type="email"
              placeholder={t('email')}
              className="w-full outline-none text-gray-700 placeholder-gray-400 bg-transparent"
              required
            />
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="flex items-start gap-4 border rounded-3xl px-6 py-4 focus-within:ring-2 focus-within:ring-olive-dark transition-all"
          >
            <FaComment className="text-gray-400 text-xl mt-1" />
            <textarea
              placeholder={t('message')}
              className="w-full outline-none text-gray-700 placeholder-gray-400 bg-transparent resize-none h-32"
              required
            />
          </motion.div>

          <motion.button
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            type="submit"
            disabled={loading}
            className={`bg-[var(--color-olive-dark)] text-white font-semibold py-4 rounded-full text-lg hover:bg-olive-dark/90 transition-colors shadow-md ${loading ? 'opacity-60 cursor-not-allowed' : ''
              }`}
          >
            {loading ? 'جاري الإرسال...' : t('sendButton')}
          </motion.button>
        </form>
      </motion.div>
    </section>
  );
}